<script setup lang="ts">
import type { ChatTransaction } from '@/types/conversation.type'

withDefaults(
  defineProps<{
    activeTx: ChatTransaction | null
    assistantLabel?: string
  }>(),
  { assistantLabel: 'Sales Agent' },
)
</script>

<template>
  <div v-if="activeTx" class="message">
    <div class="assistant-message-container">
      <div class="assistant-header">
        <div class="assistant-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="url(#gradient-loading)" />
            <path
              d="M12 5.5L14.5 8H16V10.5L18.5 13V15.5L16 18H14.5L12 20.5L9.5 18H8L5.5 15.5V13L8 10.5V8H9.5L12 5.5Z"
              fill="white"
              opacity="0.9"
            />
            <circle cx="12" cy="12" r="3" fill="url(#gradient-loading-2)" />
            <defs>
              <linearGradient id="gradient-loading" x1="0" y1="0" x2="24" y2="24">
                <stop offset="0%" stop-color="#667eea" />
                <stop offset="100%" stop-color="#764ba2" />
              </linearGradient>
              <linearGradient id="gradient-loading-2" x1="8" y1="8" x2="16" y2="16">
                <stop offset="0%" stop-color="#f093fb" />
                <stop offset="100%" stop-color="#f5576c" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <span class="assistant-label">{{ assistantLabel }}</span>
      </div>
      <div class="assistant-message-text">
        <div class="assistant-message-text-body">
          {{ activeTx.inProgressLabel || 'Preparing answer...' }}
        </div>
        <div class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.message {
  margin-bottom: 16px;
}

.assistant-message-container {
  .assistant-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .assistant-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .assistant-label {
    font-size: var(--font-size-caption);
    color: #909399;
    font-weight: 500;
  }

  .assistant-message-text {
    background: #f0f0f0;
    border-radius: 18px 18px 18px 4px;
    padding: 4px 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    .assistant-message-text-body {
      font-size: var(--font-size-body);
      line-height: var(--line-height-normal);
      color: #333;
      word-wrap: break-word;
    }

    /* Typing Indicator */
    .typing-indicator {
      display: flex;
      gap: 3px;
      margin-top: 6px;

      span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #666;
        animation: typing 1.2s ease-in-out infinite;

        &:nth-child(1) {
          animation-delay: 0s;
        }

        &:nth-child(2) {
          animation-delay: 0.15s;
        }

        &:nth-child(3) {
          animation-delay: 0.3s;
        }
      }
    }
  }
}

@keyframes typing {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1.1);
  }
}
</style>
